/**
 * PublishingCadenceSection — rhythm at a glance.
 *
 * Deterministic labels — no exposed floating-point rates. The user
 * sees words a marketing director would use: "Weekly", "Biweekly",
 * "Monthly", "Not scheduled".
 */
import { Card } from "@/shared/ui/card";
import type { CadenceBlock } from "../../types";

export function PublishingCadenceSection({
  cadence,
}: {
  cadence: CadenceBlock;
}): JSX.Element {
  const rows = [
    {
      label: "Social posts",
      value: `${cadence.posts_per_week} per week`,
      testId: "cadence-posts",
    },
    {
      label: "Email",
      value: cadence.email_frequency,
      testId: "cadence-email",
    },
    {
      label: "Blog",
      value: cadence.blog_frequency,
      testId: "cadence-blog",
    },
    {
      label: "Video",
      value: cadence.video_frequency,
      testId: "cadence-video",
    },
    {
      label: "Landing pages",
      value: cadence.landing_page_count > 0
        ? `${cadence.landing_page_count} planned`
        : "Not planned",
      testId: "cadence-landing",
    },
  ];

  return (
    <section data-testid="strategy-publishing-cadence">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          Publishing Cadence
        </p>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
          The rhythm we&apos;ll keep
        </h2>
      </div>

      <Card className="p-6">
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li
              key={r.label}
              className="flex items-center justify-between py-3 text-sm"
              data-testid={r.testId}
            >
              <span className="text-muted-foreground">{r.label}</span>
              <span className="font-medium">{r.value}</span>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
